import React, {
	createContext,
	useCallback,
	useContext,
	useEffect,
} from "react";
import { useLocalStorage } from "./hooks/useLocalStorage";

// Define available themes
export const THEMES = {
	LINEAR: "linear",
	LINEAR_LIGHT: "linear-light",
};

export const THEME_OPTIONS = [THEMES.LINEAR, THEMES.LINEAR_LIGHT];

const ThemeContext = createContext(null);
const DEFAULT_THEME = THEMES.LINEAR;

const isLinearTheme = (theme) => THEME_OPTIONS.includes(theme);

export const ThemeProvider = ({ children }) => {
	const [storedTheme, setStoredTheme] = useLocalStorage("theme", DEFAULT_THEME);
	const theme = isLinearTheme(storedTheme) ? storedTheme : DEFAULT_THEME;

	useEffect(() => {
		if (storedTheme !== theme) setStoredTheme(theme);
	}, [storedTheme, setStoredTheme, theme]);

	const setTheme = useCallback(
		(nextTheme) => {
			setStoredTheme(isLinearTheme(nextTheme) ? nextTheme : DEFAULT_THEME);
		},
		[setStoredTheme],
	);

	useEffect(() => {
		// Remove all theme classes first
		document.documentElement.classList.remove(
			THEMES.LINEAR,
			THEMES.LINEAR_LIGHT,
		);

		// Add the current theme class
		document.documentElement.classList.add(theme);

		// Update color-scheme
		document.documentElement.style.colorScheme =
			theme === THEMES.LINEAR_LIGHT ? "light" : "dark";
	}, [theme]);

	return (
		<ThemeContext.Provider value={{ theme, setTheme }}>
			{children}
		</ThemeContext.Provider>
	);
};

export const useTheme = () => {
	const context = useContext(ThemeContext);
	if (context === null) {
		throw new Error("useTheme must be used within a ThemeProvider");
	}
	return context;
};
